// src/data/GoalsData.js
import { SupabaseClient } from '../supabaseClient';

/**
 * Fetches all goals for a given profile.
 * @param {string} profileId
 * @returns {Promise<Array>} list of goal objects
 */
export async function fetchGoals(profileId) {
    const { data, error } = await SupabaseClient
        .from('goals')
        .select('*')
        .eq('profile_id', profileId)
        .order('created_at', { ascending: false });
    return {data, error};
}

/**
 * Creates a new goal entry in the database.
 * @param {Object} goalPayload
 * @returns {Promise<{ data: any, error: any }>}
 */
export async function createGoal(goalPayload) {
    const { data, error } = await SupabaseClient
        .from('goals')
        .insert(goalPayload);
    return { data, error };
}

/**
 * Computes progress map for numeric goals.
 * Returns an object mapping goal.id to { progress, percent }.
 * @param {string} profileId
 * @param {Array} goals
 * @returns {Promise<Object>} progressMap
 */
export async function getProgressMap(profileId, goals) {
    const map = {};
    const todayISO = new Date().toISOString().split('T')[0];

    for (const goal of goals) {
        if (goal.goal_type !== 'numeric') continue;

        let progress = 0;

        if (goal.data_source === 'weight_entries') {
            const { data, error } = await SupabaseClient
                .from('weight_entries')
                .select('weight_kg')
                .eq('profile_id', profileId)
                .gte('date', goal.start_date)
                .lte('date', goal.end_date || todayISO)
                .order('date', { ascending: false })
                .limit(1);
            if (error) {
                return { map, error };
            }
            if (!error && data.length) progress = data[0].weight_kg;
        }

        if (goal.data_source === 'workouts') {
            const { data, error } = await SupabaseClient
                .from('workouts')
                .select(goal.data_field)
                .eq('profile_id', profileId)
                .gte('logged_at', goal.start_date)
                .lte('logged_at', goal.end_date || todayISO);
            if (error) {
                return { map, error };
            }
            if (!error) {
                progress = data.reduce((sum, row) => sum + (row[goal.data_field] || 0), 0);
            }
        }

        const percent = goal.target_value > 0
            ? Math.min((progress / goal.target_value) * 100, 100)
            : 0;

        map[goal.id] = { progress, percent };
    }

    return { map, error: null };
}


/**
 * Updates a goal by its ID.
 * @param {string} goalId
 * @param {Object} updates
 * @returns {Promise<{ data: any, error: any }>}
 */
export async function updateGoal(goalId, updates) {
    const { data, error } = await SupabaseClient
        .from('goals')
        .update(updates)
        .eq('id', goalId)
        .single();

    return { data, error };
}

/**
 * Deletes a goal by its ID.
 * @param {string} goalId
 * @returns {Promise<{ data: any, error: any }>}
 */
export async function deleteGoal(goalId) {
    const { data, error } = await SupabaseClient
        .from('goals')
        .delete()
        .eq('id', goalId)
        .single();

    return { data, error };
}